// ABOUTME: Centralized behavior utilities for icons, colors, and labels across all behavior types
// ABOUTME: Single source of truth for behavior metadata, eliminating duplication across 9+ files

import React from 'react';
import {
  StarOutlineRounded,
  AutoDelete as NukeIcon,
  SelfImprovementRounded,
} from '@mui/icons-material';
import { Box, SxProps, Theme } from '@mui/material';
import { BehaviorType, BehaviorRecordingSource } from '../types/api';
import theme from '../theme/theme';

interface IllustratedIconProps {
  sx?: SxProps<Theme>;
  'data-testid'?: string;
  className?: string;
  'aria-hidden'?: boolean | 'true' | 'false';
}

const ILLUSTRATED_ICON_DEFAULT_SIZE = 24;

/**
 * Builds a component matching the call signature every behavior-icon call site already
 * uses for MUI icons (sx.fontSize for sizing, data-testid, className for MUI Chip's
 * cloneElement-based icon styling) but rendering illustrated brand-kit PNG art instead
 * of an SVG icon font. sx is assumed to be a plain object literal, as it is at every
 * current call site — function/array sx forms are not supported. Decorative by default
 * (alt="", aria-hidden="true"), matching MUI SvgIcon's default behavior, since every call
 * site places the icon next to its own visible text or aria-label.
 */
const createIllustratedIcon = (src: string): React.FC<IllustratedIconProps> => {
  const IllustratedIcon: React.FC<IllustratedIconProps> = ({ sx, 'data-testid': dataTestId, className, 'aria-hidden': ariaHidden }) => {
    const sxObject = (sx && typeof sx === 'object' && !Array.isArray(sx) ? sx : {}) as Record<string, unknown>;
    const { fontSize, ...restSx } = sxObject;
    const size = fontSize ?? ILLUSTRATED_ICON_DEFAULT_SIZE;

    return (
      <Box
        component="img"
        src={src}
        alt=""
        aria-hidden={ariaHidden ?? true}
        data-testid={dataTestId}
        className={className}
        sx={{
          width: size,
          height: size,
          objectFit: 'contain',
          display: 'inline-block',
          verticalAlign: 'middle',
          flexShrink: 0,
          ...restSx,
        }}
      />
    );
  };
  return IllustratedIcon;
};

const StormIcon = createIllustratedIcon('/nalu/storm-256.png');
const TriangleIcon = createIllustratedIcon('/nalu/triangle-b-256.png');
const PikoIcon = createIllustratedIcon('/nalu/piko-reset-256.png');
const PonoIcon = createIllustratedIcon('/nalu/pono-256.png');
const KalaIcon = createIllustratedIcon('/nalu/kala-256.png');

export { StormIcon, TriangleIcon, PikoIcon, PonoIcon, KalaIcon };

/**
 * Centralized behavior configuration
 * Single source of truth for all behavior type metadata
 */
export const BEHAVIOR_CONFIG = {
  [BehaviorType.Star]: {
    label: 'Star',
    color: theme.palette.success.main, // Green for positive behaviors
    icon: StarOutlineRounded,
  },
  [BehaviorType.Triangle]: {
    label: 'Triangle',
    color: theme.palette.warning.main, // Orange for warnings
    icon: TriangleIcon,
  },
  [BehaviorType.Square]: {
    label: 'Storm',
    color: theme.palette.error.main, // Red for consequences
    icon: StormIcon,
  },
  [BehaviorType.FlipYourCoin]: {
    label: 'Flip Your Coin',
    color: theme.palette.info.main, // Blue for emotional regulation
    icon: PikoIcon,
  },
  [BehaviorType.MakeItRight]: {
    label: 'Make It Right',
    color: theme.palette.secondary.main, // Purple for parent actions
    icon: PonoIcon,
  },
  [BehaviorType.OneOnOneTime]: {
    label: 'Let Go',
    color: theme.palette.oneOnOneAccent,
    icon: KalaIcon,
  },
  [BehaviorType.GoalNuke]: {
    label: 'Nuclear Reset',
    color: theme.palette.destructiveAccent, // Deep orange - destructive action (Issue #1310)
    icon: NukeIcon,
  },
  [BehaviorType.Reset]: {
    label: 'Reset',
    color: theme.palette.crossingAccent, // Calm teal - deliberately not the Star's success-green (Issue #1937)
    icon: SelfImprovementRounded,
  },
} as const;

/**
 * Get the icon element for a behavior type — an MUI SvgIcon for Star/GoalNuke
 * (no illustrated art exists for these yet), or illustrated PNG art for the rest.
 *
 * @param type - The behavior type enum value
 * @param iconProps - Optional props to pass to the icon component (sx, data-testid, etc.)
 * @returns React element with the appropriate icon
 *
 * @example
 * ```tsx
 * // Basic usage
 * getBehaviorIcon(BehaviorType.Star)
 *
 * // With custom styling
 * getBehaviorIcon(BehaviorType.Triangle, { sx: { fontSize: 24 } })
 *
 * // With test ID
 * getBehaviorIcon(BehaviorType.MakeItRight, { 'data-testid': 'make-it-right-icon' })
 * ```
 */
export const getBehaviorIcon = (
  type: BehaviorType,
  iconProps?: { sx?: SxProps<Theme>; 'data-testid'?: string }
): React.ReactElement => {
  const config = BEHAVIOR_CONFIG[type];

  if (!config) {
    // Fallback to Star icon for unknown types
    return <StarOutlineRounded {...iconProps} />;
  }

  const Icon = config.icon;
  const defaultSx: SxProps<Theme> = { color: config.color };
  const mergedSx = iconProps?.sx
    ? { ...defaultSx, ...iconProps.sx }
    : defaultSx;

  return <Icon {...iconProps} sx={mergedSx} />;
};

/**
 * Get the display label for a behavior type
 *
 * @param type - The behavior type enum value
 * @returns Human-readable label string
 *
 * @example
 * ```tsx
 * getBehaviorLabel(BehaviorType.Star) // Returns: "Star"
 * getBehaviorLabel(BehaviorType.FlipYourCoin) // Returns: "Flip Your Coin"
 * getBehaviorLabel(BehaviorType.OneOnOneTime) // Returns: "Let Go"
 * ```
 */
export const getBehaviorLabel = (type: BehaviorType): string => {
  return BEHAVIOR_CONFIG[type]?.label || 'Unknown';
};

/**
 * Get the hex color value for a behavior type
 *
 * @param type - The behavior type enum value
 * @returns Hex color string (e.g., "#10B981")
 *
 * @example
 * ```tsx
 * // Use in sx prop
 * <Box sx={{ backgroundColor: getBehaviorColor(BehaviorType.Star) }} />
 *
 * // Use in Chip component
 * <Chip sx={{ backgroundColor: getBehaviorColor(type), color: 'white' }} />
 * ```
 */
export const getBehaviorColor = (type: BehaviorType): string => {
  return BEHAVIOR_CONFIG[type]?.color || theme.palette.success.main;
};

/**
 * Type guard to check if a value is a valid BehaviorType
 *
 * @param value - Value to check
 * @returns True if value is a valid BehaviorType enum value
 */
export const isValidBehaviorType = (value: unknown): value is BehaviorType => {
  return typeof value === 'number' && value in BEHAVIOR_CONFIG;
};

/**
 * Check whether a behavior type is neutral — a child-logged Crossing that has no
 * effect on counts, hearts, or the ladder. Only BehaviorType.Reset qualifies (Issue #1937).
 *
 * @param type - The behavior type enum value
 * @returns True only for BehaviorType.Reset
 */
export const isNeutralBehavior = (type: BehaviorType): boolean => {
  return type === BehaviorType.Reset;
};

/**
 * Parse string or number value to BehaviorType enum
 * Uses aggressive normalization to handle API response variations
 *
 * @param value - String (e.g., "Star", "flip your coin") or number (0-5)
 * @returns BehaviorType enum value, defaults to Star with warning for invalid inputs
 *
 * @example
 * ```tsx
 * parseBehaviorType('Star') // Returns: BehaviorType.Star (0)
 * parseBehaviorType('flip your coin') // Returns: BehaviorType.FlipYourCoin (3)
 * parseBehaviorType(1) // Returns: BehaviorType.Triangle (1)
 * parseBehaviorType('unknown') // Returns: BehaviorType.Star (0) with console warning
 * ```
 */
export const parseBehaviorType = (value: string | number): BehaviorType => {
  // Handle numeric values
  if (typeof value === 'number') {
    return value as BehaviorType;
  }

  // Handle numeric strings first
  const numericValue = parseInt(value);
  if (!isNaN(numericValue)) {
    return numericValue as BehaviorType;
  }

  // Aggressive normalization - removes spaces and special characters
  const normalized = value.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]/g, '');

  switch (normalized) {
    case 'star':
      return BehaviorType.Star;
    case 'triangle':
      return BehaviorType.Triangle;
    case 'square':
      return BehaviorType.Square;
    case 'flipyourcoin':
    case 'letsreset':
      return BehaviorType.FlipYourCoin;
    case 'makeitright':
      return BehaviorType.MakeItRight;
    case 'oneoneonetime':
    case '11time':
      return BehaviorType.OneOnOneTime;
    case 'goalnuke':
    case 'nuclearreset':
      return BehaviorType.GoalNuke;
    case 'reset':
      return BehaviorType.Reset;
    default:
      console.warn('parseBehaviorType - unknown type, defaulting to Star. Input:', value, 'normalized:', normalized);
      return BehaviorType.Star;
  }
};

/**
 * Convert BehaviorType enum to API string format
 *
 * @param type - BehaviorType enum value
 * @returns API string format (e.g., "Star", "FlipYourCoin")
 *
 * @example
 * ```tsx
 * behaviorTypeToApiString(BehaviorType.Star) // Returns: "Star"
 * behaviorTypeToApiString(BehaviorType.FlipYourCoin) // Returns: "FlipYourCoin"
 * ```
 */
export const behaviorTypeToApiString = (type: BehaviorType): string => {
  switch (type) {
    case BehaviorType.Star:
      return 'Star';
    case BehaviorType.Triangle:
      return 'Triangle';
    case BehaviorType.Square:
      return 'Square';
    case BehaviorType.FlipYourCoin:
      return 'FlipYourCoin';
    case BehaviorType.MakeItRight:
      return 'MakeItRight';
    case BehaviorType.OneOnOneTime:
      return 'OneOnOneTime';
    case BehaviorType.GoalNuke:
      return 'GoalNuke';
    case BehaviorType.Reset:
      return 'Reset';
    default:
      console.warn('behaviorTypeToApiString - unknown type:', type);
      return 'Star';
  }
};

/**
 * Resolve the human-readable "recorded by" label for a behavior record.
 *
 * A record logged from a child's own device (BehaviorRecordingSource.ChildDevice, e.g. a
 * Reset crossing) has no recording user — recordedByUserId points at a reserved system user.
 * Such records must be attributed to the child themselves, never to the system user (Issue #1937).
 *
 * @param record - The behavior record fields relevant to attribution. `source` may arrive as a
 *   number (BehaviorRecord) or a numeric string (transformed table entries).
 * @returns The child's name (or "Themselves") for ChildDevice records, otherwise the recording user's name
 */
export const getRecordedByDisplayName = (record: {
  source?: number | string;
  recordedByUserName?: string;
  childName?: string;
}): string => {
  const sourceValue =
    typeof record.source === 'string' ? parseInt(record.source, 10) : record.source;

  if (sourceValue === BehaviorRecordingSource.ChildDevice) {
    return record.childName || 'Themselves';
  }

  return record.recordedByUserName || 'Unknown User';
};

/**
 * Get all BehaviorType enum values from BEHAVIOR_CONFIG
 * Useful for initializing filters with all types selected
 *
 * @returns Array of all BehaviorType enum values
 *
 * @example
 * ```tsx
 * getAllBehaviorTypes() // Returns: [0, 1, 2, 3, 4, 5]
 * ```
 */
export const getAllBehaviorTypes = (): BehaviorType[] => {
  return Object.keys(BEHAVIOR_CONFIG).map(k => parseInt(k) as BehaviorType);
};

/**
 * Shared filter state interface for admin behavior filtering
 */
export interface BehaviorFilterState {
  selectedChildId: number | null;
  selectedBehaviorTypes: BehaviorType[];
}

/**
 * Get default filter state with all children and all behavior types selected
 *
 * @returns Default BehaviorFilterState
 *
 * @example
 * ```tsx
 * const filterState = getDefaultBehaviorFilterState();
 * // Returns: { selectedChildId: null, selectedBehaviorTypes: [0, 1, 2, 3, 4, 5] }
 * ```
 */
export const getDefaultBehaviorFilterState = (): BehaviorFilterState => ({
  selectedChildId: null, // "All Children"
  selectedBehaviorTypes: getAllBehaviorTypes()
});
